import Transition from '../Transition/Transition.jsx';
import React, { Fragment, useState, useRef, useEffect } from 'react';

import './Onboarding.scss';

import { isOther } from '../../helpers/user';
import icons from '../../shared/assets/icons';
import sendEvent from '../../helpers/tracking';
import images from '../../shared/assets/images';
import { getString } from '../../helpers/styles';
import { format, buildDateString } from '../../helpers/date';
import CalendarPopup from '../CalendarPopup/CalendarPopup.jsx';
import CalculationTool from '../CalculationTool/CalculationTool.jsx';
import { getItem, setItem, removeItem } from '../../helpers/sessionStorage';
import {
  sliceLength,
  setCustomValidity,
  resetValidity
} from '../../helpers/form';

const { logo } = images;
const { Calendar } = icons;

const steps = [
  {
    Component: UserType,
    skip: false,
    event: 'reason',
    hash: 'account-type'
  },
  {
    Component: EstimatedDate,
    skip: false,
    event: 'edd',
    hash: 'edd'
  },
  {
    Component: PartnerEmail,
    skip: true,
    event: 'partnerinvite',
    hash: 'partner-email'
  },
  {
    Component: OrganizationList,
    skip: false,
    event: 'provider',
    hash: 'provider'
  }
];

const TYPES = {
  PREG: 'Ik ben zwanger',
  PARTNER: 'Mijn partner is zwanger',
  PREP: 'We willen zwanger worden',
  PROVIDER: 'Ik ben zorgverlener',
  OTHER: 'Anders'
};

export default function Onboarding(props) {
  const { edd, accountType, emailPartner, postcode, provider } = props.user;

  const [limit, setLimit] = useState(4);
  const [currentStep, changeStep] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const step = steps[currentStep] || steps[steps.length - 1];

  const { Component } = step;

  const isLastStep = currentStep === steps.length - 1;

  const [previousSteps, setPreviousSteps] = useState(new Set());

  const [userData, setUserData] = useState({
    edd,
    accountType,
    emailPartner,
    postcode,
    provider: provider && provider.accountName
  });

  const [iOSdatepicker, useIOSdatepicker] = useState(false);

  const [calculatedDueDate, setCalculatedDueDate] = useState(null);

  const [dueDateCalculator, showDueDateCalculator] = useState(false);

  const [dueDateWarning, showDueDateWarning] = useState(false);

  const stepIsDetermined = currentStep !== null;

  useEffect(() => {
    showTargetStep();

    showIOSDatePicker();
  }, []);

  useEffect(() => {
    if (stepIsDetermined && steps[currentStep])
      history.replaceState({}, '', `onboarding#${steps[currentStep].hash}`);

    const isEddStep = currentStep === 1;

    if (isEddStep) showDueDateCalculator(getItem('showDueDateCalculator'));
  }, [currentStep]);

  return stepIsDetermined ? (
    <section id="onboarding">
      <div id="onboarding_content">
        <button onClick={() => logout()} className="onboarding_close_button">
          &times;
        </button>

        <div id="onboarding_logo">
          <img src={logo} alt="logo" />
        </div>

        {dueDateCalculator ? (
          <React.Fragment>
            <CalculationTool
              type="dueDate"
              onboarding={true}
              showDueDateWarning={showDueDateWarning}
              nextOnboardingStep={(date) => saveDueDate(date)}
              actionOnCalculating={() =>
                sendEvent('Onboarding', 'Bereken', 'Step2')
              }
            />

            {dueDateWarning && (
              <div className="onboarding_due_date_warning">
                Je uitgerekende datum is niet correct, bereken opnieuw
              </div>
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            <form onSubmit={(e) => nextStep(e)}>
              <div id="onboarding_component">
                <Component
                  limit={limit}
                  setLimit={setLimit}
                  userData={userData}
                  search={() => search()}
                  changeStep={changeStep}
                  setUserData={setUserData}
                  iOSdatepicker={iOSdatepicker}
                  validationError={validationError}
                  organizations={props.organizations}
                  changeUserData={props.changeUserData}
                  calculatedDueDate={calculatedDueDate}
                  setValidationError={setValidationError}
                  userProvider={provider && provider.displayName}
                  changeUserDataWithRedirect={props.changeUserDataWithRedirect}
                />
              </div>

              {currentStep === 1 && (
                <p
                  onClick={() => {
                    showDueDateCalculator(true);

                    setItem('showDueDateCalculator', true);

                    setPreviousSteps(previousSteps.add(currentStep));
                  }}
                  className="onboarding_due_date_tool_link"
                >
                  Bereken hier je uitgerekende datum
                </p>
              )}

              <input
                type="submit"
                value="Volgende"
                className={getString([
                  {
                    value: 'onboarding_button',
                    condition: true
                  },
                  {
                    value: 'onboarding_button_disabled',
                    condition: isLastStep && !props.organizations && !provider
                  }
                ])}
              />
            </form>

            {step.skip && (
              <p
                id="onboarding_skip_label"
                onClick={() => {
                  sendEvent('Onboarding', 'Overslaan3', 'Step3');

                  changeStep(currentStep + 1);

                  setPreviousSteps(previousSteps.add(currentStep));

                  props.changeUserData({
                    variables: { partnerInviteStatus: 'SKIP' }
                  });
                }}
              >
                Overslaan
              </p>
            )}
          </React.Fragment>
        )}

        <ul id="onboarding_steps">
          {steps.map((_, idx) => (
            <li
              key={idx}
              className={getString([
                {
                  value: 'onboarding_current_step',
                  condition: currentStep === idx
                },
                {
                  value: 'onboarding_previous_step',
                  condition: previousSteps.has(idx)
                }
              ])}
              onClick={() => {
                if (previousSteps.has(idx)) {
                  changeStep(idx);

                  closeDueDateCalculator();

                  userData.provider = null;
                }
              }}
            >
              &#8226;
            </li>
          ))}
        </ul>
      </div>
    </section>
  ) : null;

  function nextStep(e) {
    e.preventDefault();

    const { emailPartner, eddDate, iOSEddDate } = e.target;

    if (isLastStep) {
      if (document.activeElement.name === 'postcode') return search();

      props.addUserEvent({
        variables: { type: 'onboarding-completed', data: '{}' }
      });

      props.changeUserDataWithRedirect({
        variables: {
          weekMail: 'Y',
          provider: userData.provider || '',
          ...(!userData.edd && { eddStatus: 'SKIP' }),
          providerStatus: userData.provider ? 'OK' : 'SKIP'
        }
      });
    }

    if (isOther(userData))
      return props.changeUserDataWithRedirect({
        variables: { weekMail: 'Y', eddStatus: 'SKIP' }
      });

    sendEvent(
      'Onboarding',
      `Volgende${currentStep + 1}`,
      `Step${currentStep + 1}`
    );

    if (
      currentStep === 0 &&
      ['PREP', 'PROVIDER'].includes(userData.accountType)
    ) {
      changeStep(currentStep + 3);

      const newSet = new Set(previousSteps);

      newSet.add(0);
      newSet.delete(1);
      newSet.delete(2);

      setPreviousSteps(newSet);
    } else {
      if (eddDate) {
        const validationErrorMessage = validateEstimatedDateRange(eddDate);

        if (validationErrorMessage)
          return setValidationError(validationErrorMessage);
      }

      changeStep(currentStep + 1);

      setPreviousSteps(previousSteps.add(currentStep));
    }

    if (currentStep === 1 && userData.accountType === 'PARTNER')
      return skipPartnerEmailStep();

    if (eddDate) {
      const edd = eddDate.value.split('-').reverse().join('-');

      setUserData({ ...userData, edd });

      setCalculatedDueDate(eddDate.value);

      props.changeUserData({ variables: { edd, eddStatus: 'OK' } });
    }

    if (iOSEddDate) {
      setUserData({ ...userData, edd: iOSEddDate.value });

      setCalculatedDueDate(iOSEddDate.value);

      props.changeUserData({
        variables: { edd: iOSEddDate.value, eddStatus: 'OK' }
      });
    }

    if (emailPartner) {
      setUserData({ ...userData, emailPartner: emailPartner.value });

      props.changeUserData({
        variables: {
          emailPartner: emailPartner.value,
          partnerInviteStatus: 'OK'
        }
      });
    }
  }

  function search() {
    const postalcodeField = document.getElementById('onboarding_postcode');

    const postalcode = postalcodeField.value.trim();

    if (!postalcode) {
      setCustomValidity(
        postalcodeField,
        'Vertel ons de 4 cijfers van je postcode en klik op zoeken'
      );

      postalcodeField.reportValidity();

      return setValidationError('Vul eerst je postcode in om verder te gaan');
    }

    setLimit(limit);
    setValidationError(null);

    props.searchOrganizations({ variables: { postalcode, distance: 1000 } });
  }

  function saveDueDate({ date, week }) {
    if (week > 40) return showDueDateWarning(true);

    const day = date.getDate();
    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    const edd = `${year}-${
      month < 10 ? `0${month}` : month
    }-${day < 10 ? `0${day}` : day}`;

    setUserData({ ...userData, edd });

    setCalculatedDueDate(edd);

    props.changeUserData({ variables: { edd, eddStatus: 'OK' } });

    closeDueDateCalculator();

    if (currentStep === 1 && userData.accountType === 'PARTNER') {
      skipPartnerEmailStep();
    } else {
      changeStep(currentStep + 1);

      setPreviousSteps(previousSteps.add(currentStep));
    }
  }

  function skipPartnerEmailStep() {
    changeStep(currentStep + 2);

    const newSet = new Set(previousSteps);

    newSet.add(0);
    newSet.add(1);
    newSet.delete(2);

    setPreviousSteps(newSet);
  }

  function closeDueDateCalculator() {
    showDueDateCalculator(false);

    removeItem('showDueDateCalculator');
  }

  function showTargetStep() {
    const hash = window.location.hash.slice(1);

    let step = steps.findIndex((step) => step.hash === hash);

    if (!~step) {
      changeStep(0);
    } else {
      changeStep(step);

      const previousSteps = new Set();

      while (step > 0) previousSteps.add(--step);

      setPreviousSteps(previousSteps);
    }
  }

  function showIOSDatePicker() {
    const iOS = /iPad|iPhone|iPod/.test(navigator.platform);

    if (iOS) useIOSdatepicker(true);

    document.getElementById('header').style.position = 'relative';
  }

  function validateEstimatedDateRange(eddDate) {
    const edd = eddDate.value.split('-').reverse().join('-');

    const minDate = new Date();
    const maxDate = new Date();

    minDate.setDate(minDate.getDate() - 1);
    maxDate.setDate(maxDate.getDate() + 40 * 7);

    const date = new Date(edd);

    if (date < minDate || date > maxDate)
      return 'Laat ons weten op welke dag/maand/jaar dit is.';
  }

  function logout() {
    document.cookie = 'jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

    location.href = props.config.LOGOUT_URL;
  }
}

function UserType(props) {
  useEffect(() => {
    setCustomValidity(
      document.querySelector('.onboarding_user_type'),
      'Selecteer een van deze opties'
    );
  });

  return (
    <Fragment>
      <p className="onboarding_step_title">
        Wat is de reden dat je je hebt aangemeld voor zwangerenportaal.nl?
      </p>

      <ul className="onboarding_user_types">
        {Object.keys(TYPES).map((key, id) => (
          <li
            key={id}
            onClick={() => {
              props.setUserData({ ...props.userData, accountType: key });

              props.changeUserData({
                variables: {
                  accountType: key,
                  ...(['PREG', 'PARTNER'].includes(key) && {
                    status: 'PREGNANT'
                  })
                }
              });
            }}
          >
            <label>
              <input
                required
                type="radio"
                name="user_type"
                onChange={() => {}}
                className="onboarding_user_type"
                checked={props.userData.accountType === key}
              />

              {TYPES[key]}
            </label>
          </li>
        ))}
      </ul>
    </Fragment>
  );
}

function EstimatedDate(props) {
  const date = new Date();

  const minDate = buildDateString(date);

  // 40 weeks from now
  date.setDate(date.getDate() + 40 * 7);

  const maxDate = buildDateString(date);

  const [foldedOut, togglePopup] = useState(false);

  const [selectedDate, changeSelectedDate] = useState({
    year: date.getFullYear(),
    month: date.getMonth() + 1
  });

  useEffect(() => {
    const date = document.getElementById('onboarding_date_input');

    setCustomValidity(
      date,
      'Oeps, dat ging even mis. Je uitgerekende datum ligt in de toekomst'
    );

    resetValidity(date, 'valueMissing');
  });

  useEffect(() => {
    document.body.addEventListener('click', closePopup);

    return () => document.body.removeEventListener('click', closePopup);
  }, []);

  return (
    <Fragment>
      <p className="onboarding_step_title">
        Wat is de uitgerekende datum van jouw zwangerschap?
      </p>

      {props.iOSdatepicker ? (
        <input
          required
          type="date"
          min={minDate}
          max={maxDate}
          name="iOSEddDate"
          placeholder="DD/MM/YY"
          id="onboarding_date_input"
          className="onboarding_ios_date_input"
          defaultValue={props.userData.edd || props.calculatedDueDate}
          onChange={(e) => {
            if (e.target.value) e.target.placeholder = '';
          }}
        />
      ) : (
        <React.Fragment>
          <input
            required
            name="eddDate"
            autoComplete="off"
            placeholder="Datum"
            data-validated-value=""
            id="onboarding_date_input"
            onChange={(e) => (format(e.target), props.setValidationError(null))}
            onClick={(e) => (e.target.placeholder = 'DD-MM-JJJJ')}
            defaultValue={
              props.userData.edd
                ? props.userData.edd.split('-').reverse().join('-')
                : props.calculatedDueDate
            }
            pattern="(0[1-9]|1[0-9]|2[0-9]|3[01])-(0[1-9]|1[012])-[0-9]{4}"
          />

          <span
            id="onboarding_calendar"
            onClick={() => togglePopup(!foldedOut)}
            className={getString('onboarding_icon_scaled', foldedOut)}
          >
            <Calendar />
          </span>

          <Transition on={foldedOut}>
            <CalendarPopup
              withTriangle={true}
              year={selectedDate.year}
              togglePopup={togglePopup}
              month={selectedDate.month}
              field="onboarding_date_input"
              isLimitedByPregnancyPeriod={true}
              changeSelectedDate={changeSelectedDate}
            />
          </Transition>
        </React.Fragment>
      )}

      {props.validationError && (
        <div className="onboarding_text_small">{props.validationError}</div>
      )}

      <p className="onboarding_label">
        Dit kun je altijd aanpassen in je profielinstellingen.
      </p>
    </Fragment>
  );

  function closePopup(e) {
    if (e.target.closest('#calendarPopup')) return;

    togglePopup(false);
  }
}

function PartnerEmail(props) {
  useEffect(() => {
    setCustomValidity(
      document.getElementById('onboarding_partner_email'),
      'Hier kun je het email adres van je partner invullen'
    );
  });

  return (
    <Fragment>
      <p className="onboarding_step_title">
        Breng je partner op de hoogte van de ontwikkelingen in jouw buik
      </p>

      <input
        required
        type="email"
        autoComplete="off"
        name="emailPartner"
        id="onboarding_partner_email"
        placeholder="Email adres van jouw partner"
        defaultValue={props.userData.emailPartner}
      />

      <div className="onboarding_text_small">
        <p>
          Al meer dan tienduizenden partners volgen de zwangerschap op de voet,
          met één klik ook jóuw partner.
        </p>
      </div>
    </Fragment>
  );
}

function OrganizationList(props) {
  const {
    limit,
    search,
    userData,
    setLimit,
    setUserData,
    userProvider,
    organizations,
    validationError,
    setValidationError
  } = props;

  const postcode = useRef(null);
  const noOrgCheckBox = useRef(null);

  useEffect(() => {
    setCustomValidity(
      noOrgCheckBox.current,
      'Kies dit selectievakje aan als je geen zorgverlener hebt'
    );
  });

  return (
    <Fragment>
      <p className="onboarding_step_title">Kies je zorgverlener</p>

      <div className="onboarding_organization_search">
        <label htmlFor="code">Mijn postcode</label>

        <input
          type="number"
          ref={postcode}
          name="postcode"
          placeholder="Postcode"
          id="onboarding_postcode"
          required={!userProvider}
          onChange={(e) => validate(e)}
          defaultValue={userData.postcode}
          className={getString('onboarding_value_required', validationError)}
        />

        <div
          onClick={() => {
            sendEvent('Onboarding', 'Zoeken', 'Step4');

            search();
          }}
          className="onboarding_button"
          id="onboarding_search_organization_button"
        >
          Zoeken
        </div>
      </div>

      {!organizations && userProvider && (
        <div className="onboarding_text_small">
          Geselecteerde zorgverlener: {userProvider}
        </div>
      )}

      {validationError && (
        <div className="onboarding_text_small">{validationError}</div>
      )}

      {organizations && !organizations.length && (
        <div>
          Er zijn geen verloskundige praktijken gevonden in dit postcode gebied
        </div>
      )}

      {organizations && (
        <ul className="onboarding_organization_list">
          <li>
            <label id="onboarding_no_organization_checkbox">
              <input
                type="checkbox"
                ref={noOrgCheckBox}
                required={!userData.provider}
                onClick={() => resetOrganization()}
              />
              Ik heb geen zorgverlener
            </label>
          </li>

          {organizations.slice(0, limit).map((item) =>
            ['fvp', 'onataldemo'].includes(item.accountName) ? null : (
              <li
                key={item.id}
                onClick={(e) => setOrganizationAndPostcode(e, item.accountName)}
              >
                <input type="radio" data-id={item.id} name="organization" />

                <img alt="organization-image" src={item.logoUrl} />

                <div className="onboarding_organization_info">
                  <a
                    target="_blank"
                    href={item.website}
                    rel="noopener noreferrer"
                  >
                    <p>{item.displayName}</p>
                  </a>

                  <p>(@{item.accountName})</p>

                  <span>{item.distance} kilometer</span>
                </div>
              </li>
            )
          )}
        </ul>
      )}

      {organizations && limit < organizations.length && (
        <div
          className="onboarding_loadmore_button"
          onClick={() => {
            setLimit(limit + 4);

            sendEvent('Onboarding', 'Laad Meer', 'Step4');
          }}
        >
          Laad meer
        </div>
      )}
    </Fragment>
  );

  function resetOrganization() {
    const org = document.querySelector('input[name="organization"]:checked');

    if (org) {
      org.checked = false;

      userData.provider = '';

      setUserData({ ...userData });
    }
  }

  function setOrganizationAndPostcode(e, provider) {
    e.target.closest('li').querySelector('input').checked = true;

    noOrgCheckBox.current.checked = false;

    setUserData({ ...userData, provider, postcode: postcode.current.value });
  }

  function validate(e) {
    sliceLength(e.target, 4);

    setValidationError(null);
  }
}
